import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Crown, ArrowRight, Scroll, ShieldCheck, Users, Coins, Ship, Radio,
  Hammer, Archive, Github, Sparkles, Power, TrendingUp, FileSignature, AlertTriangle,
} from "lucide-react";

function MetricCard({ label, value, icon: Icon, accent = false, testid }) {
  return (
    <div className={`glass p-5 ${accent ? "gold-glow" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-tag">{label}</div>
          <div className={`text-3xl font-black tracking-tighter mt-1 ${accent ? "gold-text" : "text-white"}`}>
            {value}
          </div>
        </div>
        <div className="w-9 h-9 flex items-center justify-center border border-[#C9A227]/30">
          <Icon className="w-4 h-4 gold-text" strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, sub, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="card-sharp p-5 hover:gold-glow transition-all group flex items-center gap-4"
    >
      <div className="w-11 h-11 flex items-center justify-center border border-[#C9A227]/30 group-hover:border-[#C9A227] transition-colors">
        <Icon className="w-5 h-5 gold-text" strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <div className="font-bold text-sm">{label}</div>
        <div className="text-[10px] label-tag mt-0.5">{sub}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-gray-500 group-hover:gold-text transition-colors" />
    </Link>
  );
}

export default function Bridge() {
  const [data, setData] = useState({
    fleetStats: null,
    forgeStats: null,
    githubStatus: null,
    armed: [],
    recentContracts: [],
    recentJournal: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.fleetStats(),
      api.forgeStats(),
      api.githubStatus(),
      api.listBroadcasts(true),
      api.listContracts(),
      api.githubLog(8),
    ]).then((results) => {
      if (!alive) return;
      const [fs, gs, gh, arm, contracts, journal] = results;
      setData({
        fleetStats: fs.status === "fulfilled" ? fs.value : null,
        forgeStats: gs.status === "fulfilled" ? gs.value : null,
        githubStatus: gh.status === "fulfilled" ? gh.value : null,
        armed: arm.status === "fulfilled" ? arm.value : [],
        recentContracts: contracts.status === "fulfilled" ? contracts.value.slice(0, 5) : [],
        recentJournal: journal.status === "fulfilled" ? journal.value : [],
      });
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const { fleetStats, forgeStats, githubStatus, armed, recentContracts, recentJournal } = data;

  return (
    <div className="space-y-12" data-testid="bridge-page">
      <section>
        <div className="inline-flex items-center gap-2 border border-[#C9A227]/40 px-3 py-1.5 mb-5">
          <Crown className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} />
          <span className="text-[10px] label-tag gold-text">Director K · Grand Command Bridge</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
          The whole <span className="gold-text">Armada</span>,
          <br />
          one console.
        </h1>
        <p className="text-gray-400 mt-4 max-w-2xl">
          Contracts forged. Fleet status. Broadcast control. Off-site journal. Every signal in the Armada,
          collected here so Director K can read the room in one glance.
        </p>
      </section>

      {/* ARMED BROADCAST BANNER */}
      {armed.length > 0 && (
        <section className="border border-emerald-500/40 bg-emerald-500/5 p-5 flex flex-wrap items-center gap-4" data-testid="bridge-armed-banner">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-emerald-400">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /> LIVE
          </span>
          <div className="flex-1 min-w-[200px]">
            <div className="font-bold text-lg">{armed[0].title}</div>
            <div className="text-xs text-gray-400">
              {armed[0].stream_type} ·{" "}
              {armed[0].target_mode === "all" ? "all hulls" :
               armed[0].target_mode === "category" ? `${armed[0].target_categories.length} categories` :
               `${armed[0].target_hull_ids.length} hulls`}
              {armed.length > 1 && ` · +${armed.length - 1} more armed`}
            </div>
          </div>
          <Link to="/stream" className="btn-ghost-gold text-xs">Manage Streams →</Link>
        </section>
      )}

      {/* TOP METRICS */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="bridge-top-metrics">
        <MetricCard label="Contracts Forged" value={forgeStats?.total_contracts ?? "—"} icon={Scroll} testid="bridge-metric-contracts" />
        <MetricCard label="Signed" value={forgeStats?.signed_contracts ?? "—"} icon={ShieldCheck} testid="bridge-metric-signed" />
        <MetricCard label="Admiral Roster" value={forgeStats?.fleet_admirals ?? "—"} icon={Users} testid="bridge-metric-admirals" />
        <MetricCard label="Fleet Hulls" value={fleetStats?.total ?? "—"} icon={Ship} accent testid="bridge-metric-fleet" />
        <MetricCard label="Hulls Live" value={fleetStats?.by_status?.live ?? 0} icon={TrendingUp} testid="bridge-metric-live" />
        <MetricCard label="Broadcasts Armed" value={armed.length} icon={Radio} testid="bridge-metric-armed" />
      </section>

      {/* QUICK ACTIONS */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="bridge-actions">
        <QuickAction to="/forge" icon={Hammer} label="Forge Contract" sub="MSA · ISA · NDA · ACD" testid="bridge-action-forge" />
        <QuickAction to="/fleet" icon={Ship} label="Fleet Console" sub="42 hulls · checklist · backup" testid="bridge-action-fleet" />
        <QuickAction to="/stream" icon={Radio} label="Stream Controller" sub="One signal · many hulls" testid="bridge-action-stream" />
        <QuickAction to="/public-forge" icon={Sparkles} label="Public Forge" sub="B2B catalog · licensing" testid="bridge-action-public" />
      </section>

      {/* TWO-COLUMN: Recent contracts + Conduit log */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-sharp p-6" data-testid="bridge-recent-contracts">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-tag">Latest from the Forge</div>
              <h2 className="text-xl font-bold mt-1">Recent Contracts</h2>
            </div>
            <Link to="/archives" className="text-xs label-tag gold-text hover:text-[#E0BC4A]">View All →</Link>
          </div>
          {loading ? (
            <div className="text-gray-500 text-sm py-6">Loading…</div>
          ) : recentContracts.length === 0 ? (
            <div className="text-gray-500 text-sm py-6">No contracts yet. <Link to="/forge" className="gold-text underline">Open the Forge.</Link></div>
          ) : (
            <div className="space-y-2">
              {recentContracts.map((c) => (
                <Link
                  key={c.id}
                  to={`/archives/${c.id}`}
                  className="flex items-center justify-between p-3 border border-white/5 hover:border-[#C9A227]/40 transition-colors"
                  data-testid={`bridge-contract-${c.id}`}
                >
                  <div>
                    <div className="font-mono text-[10px] gold-text">{c.contract_code}</div>
                    <div className="font-bold text-sm mt-0.5">{c.counterparty}</div>
                    <div className="text-[10px] text-gray-500">{c.template_name}</div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-widest px-2 py-1 border ${
                    c.status === "signed" ? "border-emerald-500/40 text-emerald-400" :
                    c.status === "sent_for_signature" ? "border-yellow-500/40 text-yellow-400" :
                    "border-white/10 text-gray-400"
                  }`}>{c.status.replace(/_/g, " ")}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card-sharp p-6" data-testid="bridge-conduit">
          <div className="flex items-center gap-2 mb-1">
            <Github className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <div className="label-tag">GitHub Conduit</div>
          </div>
          <h2 className="text-xl font-bold mb-3">
            {githubStatus?.is_live && githubStatus?.authenticated ? (
              <span className="text-emerald-400">Live · @{githubStatus.user?.login}</span>
            ) : (
              <span className="text-yellow-400">Standby</span>
            )}
          </h2>
          <div className="text-xs text-gray-500 mb-4">
            Journal log · last {recentJournal.length} entries
          </div>
          {recentJournal.length === 0 ? (
            <div className="text-xs text-gray-500 py-4">No commits yet.</div>
          ) : (
            <div className="space-y-2">
              {recentJournal.map((e) => (
                <div key={e.id} className="text-xs border-l-2 border-[#C9A227]/30 pl-3 py-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${e.ok ? "bg-emerald-500" : "bg-red-500"}`} />
                    <span className="font-mono text-[10px] gold-text">{e.kind}</span>
                    <span className="text-gray-500 text-[10px]">{new Date(e.at).toLocaleString()}</span>
                  </div>
                  <div className="text-gray-300 mt-0.5">{e.fleet_app_name || "—"}</div>
                  {e.commit_sha && (
                    <a href={e.html_url} target="_blank" rel="noreferrer"
                       className="text-[10px] gold-text font-mono hover:text-[#E0BC4A]">
                      {e.commit_sha.slice(0, 7)} ↗
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HONEST TELEMETRY NOTE */}
      <section className="glass p-5 flex items-start gap-3" data-testid="bridge-telemetry-notice">
        <AlertTriangle className="w-4 h-4 gold-text flex-shrink-0 mt-1" strokeWidth={1.8} />
        <div className="text-sm text-gray-300 leading-relaxed">
          <strong className="text-white">Total Telemetry (traffic + revenue across 43 hulls):</strong>{" "}
          Phase 2. The hosting platform doesn't expose per-app traffic or revenue to neighbouring apps —
          each hull must push its own metrics to a telemetry ingest endpoint here. When you're ready
          to do that hull-side integration, say the word and I'll forge <code className="gold-text">POST /api/telemetry/{"{job_id}"}</code>
          {" "}plus a small server-side snippet for each hull to call once per hour.
        </div>
      </section>
    </div>
  );
}
